import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { COLORS, GAS_DEFAULTS } from '../utils/constants';

const { FiHome, FiPlus, FiTrash2, FiZap, FiActivity, FiDroplet, FiX, FiEdit2, FiBriefcase, FiCopy } = FiIcons;

const ObjectMeterManager = ({ 
  objects, 
  activeObjId, 
  activeMeterId,
  companyName,
  onSelectObj, 
  onSelectMeter, 
  onAddObj,
  onUpdateObj,
  onDeleteObj, 
  onAddMeter,
  onUpdateMeter,
  onDeleteMeter,
  onSetCompanyName
}) => {
  const [isAddingObj, setIsAddingObj] = useState(false);
  const [isEditingObj, setIsEditingObj] = useState(null);
  const [isAddingMeter, setIsAddingMeter] = useState(false);
  const [isEditingMeter, setIsEditingMeter] = useState(null);
  
  const [newObjName, setNewObjName] = useState('');
  const [editObjName, setEditObjName] = useState('');
  
  const [newMeterName, setNewMeterName] = useState('');
  const [newMeterType, setNewMeterType] = useState('electricity');
  const [gasZ, setGasZ] = useState(GAS_DEFAULTS.zNumber);
  const [gasHeat, setGasHeat] = useState(GAS_DEFAULTS.heatingValue);
  
  const [editMeterName, setEditMeterName] = useState('');
  const [editMeterType, setEditMeterType] = useState('electricity');
  const [editGasZ, setEditGasZ] = useState(GAS_DEFAULTS.zNumber);
  const [editGasHeat, setEditGasHeat] = useState(GAS_DEFAULTS.heatingValue);

  const activeObject = objects.find(o => o.id === activeObjId);

  const handleAddObj = (e) => {
    e.preventDefault();
    if(newObjName.trim()) {
      onAddObj(newObjName);
      setNewObjName('');
      setIsAddingObj(false);
    }
  };

  const handleUpdateObj = (objId, e) => {
    e.preventDefault();
    if(editObjName.trim() && onUpdateObj) {
      onUpdateObj(objId, { name: editObjName });
      setIsEditingObj(null);
      setEditObjName('');
    }
  };

  const handleDeleteObj = (objId) => {
    if(objects.length <= 1) {
      alert('Sie müssen mindestens ein Objekt behalten.');
      return;
    }
    if(window.confirm('Möchten Sie dieses Objekt wirklich unwiderruflich löschen? Alle zugehörigen Zähler und Ablesungen gehen verloren.')) {
      onDeleteObj(objId);
    }
  };

  const handleDuplicateObj = (obj) => {
    const newName = `${obj.name} (Kopie)`;
    onAddObj(newName);
  };

  const handleAddMeter = (e) => {
    e.preventDefault();
    if(newMeterName.trim()) {
      onAddMeter(activeObjId, {
        name: newMeterName,
        type: newMeterType,
        params: newMeterType === 'gas' ? { zNumber: gasZ, heatingValue: gasHeat } : {}
      });
      setNewMeterName('');
      setNewMeterType('electricity');
      setGasZ(GAS_DEFAULTS.zNumber);
      setGasHeat(GAS_DEFAULTS.heatingValue);
      setIsAddingMeter(false);
    }
  };

  const handleUpdateMeter = (meterId, e) => {
    e.preventDefault();
    if(editMeterName.trim()) {
      onUpdateMeter(activeObjId, meterId, {
        name: editMeterName,
        type: editMeterType,
        params: editMeterType === 'gas' ? { zNumber: editGasZ, heatingValue: editGasHeat } : {}
      });
      setIsEditingMeter(null);
      setEditMeterName('');
      setEditMeterType('electricity');
      setEditGasZ(GAS_DEFAULTS.zNumber);
      setEditGasHeat(GAS_DEFAULTS.heatingValue);
    }
  };

  const handleDeleteMeter = (meterId) => {
    if(window.confirm('Möchten Sie diesen Zähler wirklich unwiderruflich löschen? Alle zugehörigen Ablesungen gehen verloren.')) {
      onDeleteMeter(activeObjId, meterId);
    }
  };

  const handleDuplicateMeter = (meter) => {
    onAddMeter(activeObjId, {
      name: `${meter.name} (Kopie)`,
      type: meter.type,
      params: meter.params || {}
    });
  };

  const startEditObj = (obj) => {
    setEditObjName(obj.name);
    setIsEditingObj(obj.id);
  };

  const startEditMeter = (meter) => {
    setEditMeterName(meter.name);
    setEditMeterType(meter.type);
    if(meter.type === 'gas' && meter.params) {
      setEditGasZ(meter.params.zNumber || GAS_DEFAULTS.zNumber);
      setEditGasHeat(meter.params.heatingValue || GAS_DEFAULTS.heatingValue);
    }
    setIsEditingMeter(meter.id);
  };

  const getMeterIcon = (type) => {
    switch(type) {
      case 'electricity': return FiZap;
      case 'gas': return FiActivity;
      case 'water':
      case 'waterCold':
      case 'waterWarm':
        return FiDroplet;
      default: return FiActivity;
    }
  };

  const getMeterColor = (type) => {
    return COLORS[type] || COLORS.primary;
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-4 md:p-6 border-t-4" style={{ borderColor: COLORS.primary }}>
      
      {/* Company Name Field */}
      <div className="mb-6 pb-4 border-b border-gray-200">
        <label className="flex items-center gap-2 text-sm font-bold text-gray-600 mb-2">
          <SafeIcon icon={FiBriefcase} />
          Name / Unternehmen
        </label>
        <input 
          type="text"
          value={companyName}
          onChange={(e) => onSetCompanyName(e.target.value)}
          placeholder="z.B. Akademie für integrales Marketing und soziale Kommunikation e.V."
          className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-orange-500 focus:outline-none transition-colors text-sm"
        />
        <p className="text-xs text-gray-500 mt-1">
          Wird im PDF-Export und CSV-Export angezeigt
        </p>
      </div>

      {/* Object Selection */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-2 text-lg font-bold" style={{ color: COLORS.primary }}>
          <SafeIcon icon={FiHome} />
          <span>Objekt:</span>
        </div>
        
        <div className="flex-1 flex flex-wrap gap-2">
          {objects.map(obj => (
            <div key={obj.id} className="relative group">
              {isEditingObj === obj.id ? (
                <form onSubmit={(e) => handleUpdateObj(obj.id, e)} className="flex gap-1">
                  <input
                    autoFocus
                    value={editObjName}
                    onChange={(e) => setEditObjName(e.target.value)}
                    className="px-3 py-2 rounded-lg border-2 border-orange-400 focus:outline-none"
                    placeholder="Name"
                  />
                  <button type="submit" className="px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600">
                    ✓
                  </button>
                  <button type="button" onClick={() => setIsEditingObj(null)} className="px-3 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400">
                    ✕
                  </button>
                </form>
              ) : (
                <>
                  <button
                    onClick={() => onSelectObj(obj.id)}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                      activeObjId === obj.id 
                        ? 'text-white shadow-md' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    style={{ backgroundColor: activeObjId === obj.id ? COLORS.primary : undefined }}
                  >
                    {obj.name}
                  </button>
                  {activeObjId === obj.id && (
                    <div className="absolute -top-2 -right-2 hidden group-hover:flex gap-1 bg-white rounded-lg shadow-lg p-1 z-10">
                      <button
                        onClick={() => startEditObj(obj)}
                        className="p-1 hover:bg-blue-100 rounded text-blue-600"
                        title="Bearbeiten"
                      >
                        <SafeIcon icon={FiEdit2} size={14} />
                      </button>
                      <button
                        onClick={() => handleDuplicateObj(obj)}
                        className="p-1 hover:bg-green-100 rounded text-green-600"
                        title="Duplizieren"
                      >
                        <SafeIcon icon={FiCopy} size={14} />
                      </button>
                      <button
                        onClick={() => handleDeleteObj(obj.id)}
                        className="p-1 hover:bg-red-100 rounded text-red-600"
                        title="Löschen"
                      >
                        <SafeIcon icon={FiTrash2} size={14} />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
          <button 
            onClick={() => setIsAddingObj(true)}
            className="px-3 py-2 rounded-lg border-2 border-dashed border-gray-300 text-gray-400 hover:border-orange-400 hover:text-orange-500 transition-colors"
          >
            <SafeIcon icon={FiPlus} />
          </button>
        </div>
      </div>

      {/* Add Object Form */}
      <AnimatePresence>
        {isAddingObj && (
          <motion.form 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            onSubmit={handleAddObj} 
            className="mb-6 p-4 bg-orange-50 rounded-lg flex gap-2"
          >
            <input 
              autoFocus
              className="flex-1 px-3 py-2 rounded border border-orange-200 focus:outline-none focus:ring-2 focus:ring-orange-400"
              placeholder="Objektname (z.B. Ferienhaus)"
              value={newObjName}
              onChange={e => setNewObjName(e.target.value)}
            />
            <button type="submit" className="px-4 py-2 bg-orange-500 text-white rounded font-bold hover:bg-orange-600 transition-colors">
              Hinzufügen
            </button>
            <button 
              type="button" 
              onClick={() => {
                setIsAddingObj(false);
                setNewObjName('');
              }} 
              className="px-3 text-gray-500 hover:text-gray-700"
            >
              <SafeIcon icon={FiX}/>
            </button>
          </motion.form>
        )}
      </AnimatePresence>

      {/* Meter Selection */}
      {activeObjId && (
        <div className="border-t pt-4">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Zähler</span>
            <button 
              onClick={() => setIsAddingMeter(true)}
              className="text-sm font-bold flex items-center gap-1 hover:underline transition-all"
              style={{ color: COLORS.secondary }}
            >
              <SafeIcon icon={FiPlus} /> Neuer Zähler
            </button>
          </div>

          <div className="flex flex-wrap gap-3">
            {activeObject?.meters.map(meter => (
              <div key={meter.id} className="relative group">
                {isEditingMeter === meter.id ? (
                  <div className="p-3 bg-gray-50 rounded-xl border-2 border-orange-400">
                    <form onSubmit={(e) => handleUpdateMeter(meter.id, e)} className="space-y-2">
                      <input
                        autoFocus
                        value={editMeterName}
                        onChange={(e) => setEditMeterName(e.target.value)}
                        className="w-full px-2 py-1 rounded border focus:outline-none text-sm"
                        placeholder="Name"
                      />
                      <div className="flex gap-2">
                        <button type="submit" className="px-3 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600">
                          Speichern
                        </button>
                        <button type="button" onClick={() => setIsEditingMeter(null)} className="px-3 py-1 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400">
                          Abbrechen
                        </button>
                      </div>
                    </form>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={() => onSelectMeter(meter.id)}
                      className={`flex items-center gap-2 px-4 py-3 rounded-xl border-2 transition-all ${
                        activeMeterId === meter.id 
                          ? 'shadow-md' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      style={{
                        borderColor: activeMeterId === meter.id ? getMeterColor(meter.type) : undefined,
                        backgroundColor: activeMeterId === meter.id ? `${getMeterColor(meter.type)}15` : undefined
                      }}
                    >
                      <span className="text-lg" style={{ color: getMeterColor(meter.type) }}>
                        <SafeIcon icon={getMeterIcon(meter.type)} />
                      </span>
                      <span className={`font-medium ${activeMeterId === meter.id ? 'text-gray-900' : 'text-gray-600'}`}>
                        {meter.name}
                      </span>
                    </button>
                    {activeMeterId === meter.id && (
                      <div className="absolute -top-2 -right-2 hidden group-hover:flex gap-1 bg-white rounded-lg shadow-lg p-1 z-10">
                        <button
                          onClick={() => startEditMeter(meter)}
                          className="p-1 hover:bg-blue-100 rounded text-blue-600"
                          title="Bearbeiten"
                        >
                          <SafeIcon icon={FiEdit2} size={14} />
                        </button>
                        <button
                          onClick={() => handleDuplicateMeter(meter)}
                          className="p-1 hover:bg-green-100 rounded text-green-600"
                          title="Duplizieren"
                        >
                          <SafeIcon icon={FiCopy} size={14} />
                        </button>
                        <button
                          onClick={() => handleDeleteMeter(meter.id)}
                          className="p-1 hover:bg-red-100 rounded text-red-600"
                          title="Löschen"
                        >
                          <SafeIcon icon={FiTrash2} size={14} />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
            
            {activeObject?.meters.length === 0 && (
              <div className="text-sm text-gray-400 italic py-2">
                Noch keine Zähler. Legen Sie einen an!
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add Meter Form */}
      <AnimatePresence>
        {isAddingMeter && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }} 
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="mt-4 border-t pt-4 overflow-hidden"
          >
            <form onSubmit={handleAddMeter} className="bg-gray-50 p-4 rounded-xl border border-gray-200">
              <h4 className="font-bold text-gray-700 mb-3">Neuen Zähler anlegen</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-1">Bezeichnung</label>
                  <input 
                    required
                    className="w-full px-3 py-2 rounded border focus:outline-none focus:border-orange-500"
                    placeholder="z.B. Strom Hauptzähler"
                    value={newMeterName}
                    onChange={e => setNewMeterName(e.target.value)}
                  />
                </div>
                
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-1">Typ</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { id: 'electricity', icon: FiZap, label: 'Strom', color: COLORS.electricity },
                      { id: 'gas', icon: FiActivity, label: 'Gas', color: COLORS.gas },
                      { id: 'water', icon: FiDroplet, label: 'Wasser', color: COLORS.water }
                    ].map(type => (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => setNewMeterType(type.id)}
                        className={`p-3 rounded-lg flex flex-col items-center gap-1 transition-all ${
                          newMeterType === type.id 
                            ? 'ring-2 shadow-md' 
                            : 'bg-white border hover:border-gray-300'
                        }`}
                        style={{
                          ringColor: newMeterType === type.id ? type.color : undefined,
                          backgroundColor: newMeterType === type.id ? `${type.color}20` : undefined
                        }}
                      >
                        <SafeIcon icon={type.icon} size={20} style={{ color: type.color }} />
                        <span className="text-xs font-medium">{type.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {newMeterType === 'gas' && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="grid grid-cols-2 gap-4 mb-4 p-3 rounded text-sm"
                  style={{ backgroundColor: `${COLORS.gas}20` }}
                >
                  <div>
                    <label className="block text-xs font-bold mb-1" style={{ color: COLORS.gas }}>
                      Zustandszahl (z-Zahl)
                    </label>
                    <input 
                      type="number" 
                      step="0.0001" 
                      value={gasZ} 
                      onChange={e => setGasZ(parseFloat(e.target.value))} 
                      className="w-full p-2 rounded border focus:outline-none focus:ring-2"
                      style={{ borderColor: COLORS.gas }}
                    />
                    <p className="text-xs text-gray-600 mt-1">Steht auf Ihrer Gasrechnung</p>
                  </div>
                  <div>
                    <label className="block text-xs font-bold mb-1" style={{ color: COLORS.gas }}>
                      Brennwert (kWh/m³)
                    </label>
                    <input 
                      type="number" 
                      step="0.001" 
                      value={gasHeat} 
                      onChange={e => setGasHeat(parseFloat(e.target.value))} 
                      className="w-full p-2 rounded border focus:outline-none focus:ring-2"
                      style={{ borderColor: COLORS.gas }}
                    />
                    <p className="text-xs text-gray-600 mt-1">Steht auf Ihrer Gasrechnung</p>
                  </div>
                </motion.div>
              )}

              <div className="flex justify-end gap-2">
                <button 
                  type="button" 
                  onClick={() => {
                    setIsAddingMeter(false);
                    setNewMeterName('');
                    setNewMeterType('electricity');
                    setGasZ(GAS_DEFAULTS.zNumber);
                    setGasHeat(GAS_DEFAULTS.heatingValue);
                  }} 
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Abbrechen
                </button>
                <button 
                  type="submit" 
                  className="px-6 py-2 text-white font-bold rounded shadow-lg hover:shadow-xl transition-all"
                  style={{ backgroundColor: COLORS.secondary }}
                >
                  Speichern
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Storage Notice */}
      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
        <strong>💾 Hinweis:</strong> Ihre Daten werden lokal im Browser gespeichert. 
        Nutzen Sie regelmäßig den Export oder Magic Download, um Ihre Daten dauerhaft zu sichern.
      </div>
    </div>
  );
};

export default ObjectMeterManager;